import { useMemo } from 'react'
import { useAppSelector } from '@/hooks'
import {
  programsNewCollection,
  selectAvailablePrograms,
  selectEnrolledPrograms,
} from '@/reducers/programsNew'
import { AccountFieldKey, AccountFieldsConfig } from '@/types/utilityProgram'
import {
  DEFAULT_ACCOUNT_FIELD_LABELS,
  resolveAccountFieldsConfig,
} from './utilityAccountFields.helpers'

export type AccountFieldOverride = {
  label: string
  validationLength: number | null
}

export type AccountFieldOverrides = Record<
  AccountFieldKey,
  AccountFieldOverride
>

/**
 * Per-field label + validation-length overrides for the account-detail fields,
 * sourced from the CMS config of the user's relevant program: an enrolled program
 * if there is one, otherwise an available program. Fields the program does not
 * configure (or when there is no program at all) fall back to a default label and
 * no length — so the profile surfaces work even without a program.
 */
export function useAccountFieldCmsOverrides(): AccountFieldOverrides {
  programsNewCollection.useFetch()
  const enrolledPrograms = useAppSelector(
    useMemo(() => selectEnrolledPrograms(false), []),
  )
  const availablePrograms = useAppSelector(
    useMemo(() => selectAvailablePrograms(false), []),
  )

  const program = enrolledPrograms[0] ?? availablePrograms[0]

  return useMemo<AccountFieldOverrides>(() => {
    const config: AccountFieldsConfig = program
      ? resolveAccountFieldsConfig(program)
      : []
    const byField = new Map(config.map((f) => [f.field, f]))

    const overrideFor = (field: AccountFieldKey): AccountFieldOverride => {
      const cms = byField.get(field)
      return {
        label: cms?.label || DEFAULT_ACCOUNT_FIELD_LABELS[field],
        validationLength: cms?.validationLength ?? null,
      }
    }

    return {
      account_number: overrideFor('account_number'),
      contract_number: overrideFor('contract_number'),
      meter_number: overrideFor('meter_number'),
    }
  }, [program])
}
